import { PayRateCardResponse } from '@scale/shared/defaults';
import { UseQueryOptions, useQuery } from 'react-query';
import apiFetch from 'react-scale/lib/api-fetch';
import { useOutlierContext } from 'react-scale/components/Expert/context/useOutlierContext';
import useProjectsTodo from 'react-scale/remotasks.com/components/Expert/Dashboard/useProjectsTodo';
import _ from 'lodash';
import { NormalizedAssignment } from '@scale/shared/assignmentJson/legacy/types/NormalizedAssignment';

type UsePayRateQueryInput = {
  enabled?: boolean;
  projectId?: string;
  reviewLevel?: string;
};

const fetchPayRateCard = async (projectId?: string, reviewLevel?: string) => {
  if (_.isNil(projectId) || _.isNil(reviewLevel)) {
    return undefined;
  }
  return apiFetch<PayRateCardResponse>(null, 'internal/scaler/pay_rate_card', {
    method: 'GET',
    query: {
      projectId,
      reviewLevel,
    },
  });
};

export const usePayRateQuery = ({ enabled = true }: UsePayRateQueryInput = { enabled: true }) => {
  const { user } = useOutlierContext();

  // ensure queue is resolved before checking for pay rates
  const { data: projectsTodos, isLoading: projectsTodosLoading } = useProjectsTodo(user!);

  const isProjectsTodosLoaded = Boolean(projectsTodos) && !projectsTodosLoading;

  const projectId = projectsTodos?.projectId;
  const reviewLevel = projectsTodos?.reviewLevel;

  const isQueueLoadedAndNotEmpty =
    isProjectsTodosLoaded && !_.isNil(projectId) && !_.isNil(reviewLevel);

  return useQuery(
    ['pay-rate', projectId, reviewLevel],
    () => fetchPayRateCard(projectId, reviewLevel),
    {
      refetchOnMount: false,
      enabled: isQueueLoadedAndNotEmpty && enabled,
    },
  );
};

/**
 * useQuery wrapper for GET /pay_rate_card endpoint, intended to be used by the TasksPage.
 *
 * The currentAssignment param is not used directly for the request, but as a dependency to ensure
 * we refresh the info each time the assignment changes, so we can detect pay rate changes.
 *
 * @param param0
 * @param currentAssignment
 * @param useQueryOptions
 * @returns
 */
export const usePayRateQueryTasksPage = (
  { projectId, reviewLevel }: UsePayRateQueryInput,
  currentAssignment?: Pick<NormalizedAssignment, 'id'>, // TODO: NormalizedAssignment is currently 'deprecated' but the alternative AttemptAssignmentJson currently doesn't have id or _id fields in type
  useQueryOptions?: UseQueryOptions<
    PayRateCardResponse | undefined,
    unknown,
    PayRateCardResponse | undefined,
    string[]
  >,
) => {
  return useQuery(
    [
      'pay-rate',
      String(projectId),
      String(reviewLevel),
      // refetch payRateCard info on state assignment.id change
      String(currentAssignment?.id),
    ],
    () => fetchPayRateCard(projectId, reviewLevel),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: true,
      ...useQueryOptions,
    },
  );
};
